import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { App } from "./app";
import { CHUNK_RELOAD_FLAG, reloadOnceForStaleChunk } from "./lib/chunk-errors";
import { initSentry, Sentry } from "./lib/sentry";
import { instalarRedireccionPorSesionVencida } from "./lib/session-expired";
import { ErrorView } from "./components/ErrorView";

initSentry();

// Sesión vencida → login. Va ANTES de montar la app para que ninguna petición
// del arranque se quede con un 401 pintado en pantalla.
instalarRedireccionPorSesionVencida();

/**
 * Auto-reload ante chunk-load errors (deploy desincroniza el HTML
 * cacheado del browser con los hashes nuevos del bundle Vite).
 *
 * Estos listeners cubren los chunk errors que SÍ llegan como eventos
 * globales (preload de CSS, scripts del index). Los fallos de import()
 * lazy durante la navegación NO disparan unhandledrejection (React los
 * captura como error de render) — esos los maneja `lazyRoute` en app.tsx,
 * que llama al mismo `reloadOnceForStaleChunk`.
 *
 * Una sola recarga por sesión (sessionStorage flag) para evitar loop si el
 * deploy nuevo también está roto. Lógica compartida en `lib/chunk-errors.ts`.
 */
globalThis.addEventListener("error", (e) => {
  reloadOnceForStaleChunk(e.error ?? e.message);
});
globalThis.addEventListener("unhandledrejection", (e) => {
  reloadOnceForStaleChunk(e.reason);
});

// Si el SPA bootea OK, limpiamos el flag para que un eventual chunk
// error después (otro deploy) pueda recargar de nuevo. Damos 5s
// margen para que cargen los chunks lazy iniciales (RouteSkeleton).
globalThis.setTimeout(() => {
  globalThis.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}, 5_000);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) {
  // Fail loud: no hay root para montar — sin observabilidad ni UI
  // imposible recuperarse silenciosamente. Sentry está inicializado.
  throw new Error("OmniPOS: no se encontró #root en el DOM.");
}

createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={(props) => <ErrorView {...props} />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
